import { useState, useEffect, useCallback, useMemo } from 'react'
import ReactECharts from 'echarts-for-react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { canExportData } from '../lib/roles'
import { useToast } from '../components/ui/Toast'
import type { Site, Camera, CameraEvent, PeopleCountingStat } from '../types'
import * as XLSX from 'xlsx'

type Period = 'today' | '7d' | '30d' | 'custom'
type Tab = 'overview' | 'people' | 'vehicles' | 'events'

const EVENT_COLORS: Record<string, string> = {
  face: '#8b5cf6',
  body: '#3b82f6',
  license_plate: '#f59e0b',
  vehicle: '#ef4444',
  motion: '#6b7280',
  intrusion: '#dc2626',
  line_cross: '#06b6d4',
  enter_area: '#10b981',
  leave_area: '#f97316',
}

const EVENT_LABELS: Record<string, string> = {
  face: 'Face',
  body: 'Person',
  license_plate: 'License Plate',
  vehicle: 'Vehicle',
  motion: 'Motion',
  intrusion: 'Intrusion',
  line_cross: 'Line Cross',
  enter_area: 'Enter Area',
  leave_area: 'Leave Area',
}

function dateRange(period: Period, customStart?: string, customEnd?: string): { start: Date; end: Date } {
  const end = new Date()
  let start: Date
  switch (period) {
    case 'today':
      start = new Date(end.getFullYear(), end.getMonth(), end.getDate())
      break
    case '7d':
      start = new Date(end.getTime() - 7 * 86400000)
      break
    case '30d':
      start = new Date(end.getTime() - 30 * 86400000)
      break
    case 'custom':
      start = customStart ? new Date(customStart) : new Date(end.getTime() - 86400000)
      return { start, end: customEnd ? new Date(customEnd) : end }
    default:
      start = new Date(end.getTime() - 86400000)
  }
  return { start, end }
}

function pad(n: number) { return n.toString().padStart(2, '0') }
function toDateStr(d: Date) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` }

export function AnalyticsPage() {
  const { profile } = useAuth()
  const { toast } = useToast()
  const role = profile?.role ?? 'viewer'
  const isRvAdmin = role === 'rv_admin'
  const miEnabled = profile?.mi_enabled || isRvAdmin

  // Filters
  const [tab, setTab] = useState<Tab>('overview')
  const [period, setPeriod] = useState<Period>('7d')
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null)
  const [selectedSiteId, setSelectedSiteId] = useState<string | 'all'>('all')
  const [selectedCameraId, setSelectedCameraId] = useState<string | 'all'>('all')

  // Data
  const [orgs, setOrgs] = useState<{ id: string; name: string }[]>([])
  const [sites, setSites] = useState<Site[]>([])
  const [cameras, setCameras] = useState<Camera[]>([])
  const [events, setEvents] = useState<CameraEvent[]>([])
  const [peopleCounts, setPeopleCounts] = useState<PeopleCountingStat[]>([])
  const [loading, setLoading] = useState(true)

  // Load orgs (rv_admin sees all, others see own)
  useEffect(() => {
    if (!profile) return
    if (isRvAdmin) {
      supabase.from('organizations').select('id, name').order('name')
        .then(({ data }) => {
          setOrgs((data ?? []) as { id: string; name: string }[])
          if (!selectedOrgId && data?.length) setSelectedOrgId(data[0].id)
        })
    } else {
      setSelectedOrgId(profile.org_id)
      if (profile.org_name) setOrgs([{ id: profile.org_id!, name: profile.org_name }])
    }
  }, [profile, isRvAdmin])

  // Load sites for selected org
  useEffect(() => {
    if (!selectedOrgId) return
    supabase.from('sites').select('*').eq('org_id', selectedOrgId).order('name')
      .then(({ data }) => setSites((data ?? []) as Site[]))
  }, [selectedOrgId])

  // Load cameras for selected site(s)
  useEffect(() => {
    if (!selectedOrgId || sites.length === 0) { setCameras([]); return }
    let query = supabase.from('cameras').select('*').order('name')
    if (selectedSiteId !== 'all') {
      query = query.eq('site_id', selectedSiteId)
    } else {
      query = query.in('site_id', sites.map(s => s.id))
    }
    query.then(({ data }) => setCameras((data ?? []) as Camera[]))
  }, [selectedOrgId, selectedSiteId, sites])

  // Load events & people counts
  const loadData = useCallback(async () => {
    if (!selectedOrgId || sites.length === 0) return
    setLoading(true)

    const { start, end } = dateRange(period, customStart, customEnd)
    const siteIds = selectedSiteId === 'all' ? sites.map(s => s.id) : [selectedSiteId]

    // Events query
    let evtQuery = supabase
      .from('camera_events')
      .select('*')
      .eq('org_id', selectedOrgId)
      .in('site_id', siteIds)
      .gte('timestamp', start.toISOString())
      .lte('timestamp', end.toISOString())
      .order('timestamp', { ascending: true })
      .limit(5000)

    if (selectedCameraId !== 'all') {
      evtQuery = evtQuery.eq('camera_id', selectedCameraId)
    }

    // People counts query
    let pcQuery = supabase
      .from('people_counting_stats')
      .select('*')
      .eq('org_id', selectedOrgId)
      .in('site_id', siteIds)
      .gte('period_start', start.toISOString())
      .lte('period_start', end.toISOString())
      .order('period_start', { ascending: true })

    if (selectedCameraId !== 'all') {
      pcQuery = pcQuery.eq('camera_id', selectedCameraId)
    }

    const [evtResult, pcResult] = await Promise.all([evtQuery, pcQuery])
    setEvents((evtResult.data ?? []) as CameraEvent[])
    setPeopleCounts((pcResult.data ?? []) as PeopleCountingStat[])
    setLoading(false)
  }, [selectedOrgId, selectedSiteId, selectedCameraId, sites, period, customStart, customEnd])

  useEffect(() => { loadData() }, [loadData])

  // ── Computed chart data ──

  // Event type breakdown (pie)
  const eventTypeCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    events.forEach(e => { counts[e.event_type] = (counts[e.event_type] ?? 0) + 1 })
    return Object.entries(counts)
      .map(([type, count]) => ({ name: EVENT_LABELS[type] ?? type, value: count, type }))
      .sort((a, b) => b.value - a.value)
  }, [events])

  // Events over time (bar)
  const eventsOverTime = useMemo(() => {
    const buckets: Record<string, Record<string, number>> = {}
    events.forEach(e => {
      const day = toDateStr(new Date(e.timestamp))
      if (!buckets[day]) buckets[day] = {}
      buckets[day][e.event_type] = (buckets[day][e.event_type] ?? 0) + 1
    })
    const days = Object.keys(buckets).sort()
    const types = [...new Set(events.map(e => e.event_type))]
    return { days, types, buckets }
  }, [events])

  // People counting over time (line)
  const peopleOverTime = useMemo(() => {
    const buckets: Record<string, { enter: number; exit: number }> = {}
    peopleCounts.forEach(pc => {
      const day = toDateStr(new Date(pc.period_start))
      if (!buckets[day]) buckets[day] = { enter: 0, exit: 0 }
      buckets[day].enter += pc.enter_count
      buckets[day].exit += pc.exit_count
    })
    const days = Object.keys(buckets).sort()
    return { days, data: buckets }
  }, [peopleCounts])

  // Top cameras by event count
  const topCameras = useMemo(() => {
    const counts: Record<string, number> = {}
    events.forEach(e => { counts[e.camera_id] = (counts[e.camera_id] ?? 0) + 1 })
    return Object.entries(counts)
      .map(([camId, count]) => ({
        name: cameras.find(c => c.id === camId)?.name ?? camId.slice(0, 8),
        value: count,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10)
  }, [events, cameras])

  // Vehicle/plate summary
  const vehicleSummary = useMemo(() => {
    const plates: Record<string, number> = {}
    const brands: Record<string, number> = {}
    events.filter(e => e.event_type === 'license_plate' || e.event_type === 'vehicle').forEach(e => {
      const meta = e.metadata as Record<string, unknown>
      if (meta.plate_number) plates[meta.plate_number as string] = (plates[meta.plate_number as string] ?? 0) + 1
      if (meta.car_brand) brands[meta.car_brand as string] = (brands[meta.car_brand as string] ?? 0) + 1
    })
    return {
      topPlates: Object.entries(plates).sort((a, b) => b[1] - a[1]).slice(0, 15),
      topBrands: Object.entries(brands).sort((a, b) => b[1] - a[1]).slice(0, 10),
    }
  }, [events])

  // ── Export ──
  const handleExport = useCallback(() => {
    if (!canExportData(role)) {
      toast('You do not have permission to export data', 'error')
      return
    }
    const wb = XLSX.utils.book_new()

    // Events sheet
    if (events.length > 0) {
      const evtRows = events.map(e => ({
        Timestamp: new Date(e.timestamp).toLocaleString(),
        Type: EVENT_LABELS[e.event_type] ?? e.event_type,
        Camera: cameras.find(c => c.id === e.camera_id)?.name ?? e.camera_id,
        Site: sites.find(s => s.id === e.site_id)?.name ?? e.site_id,
        Confidence: e.confidence ?? '',
        Plate: (e.metadata as Record<string, unknown>).plate_number ?? '',
        Brand: (e.metadata as Record<string, unknown>).car_brand ?? '',
      }))
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(evtRows), 'Events')
    }

    // People counting sheet
    if (peopleCounts.length > 0) {
      const pcRows = peopleCounts.map(pc => ({
        Period: new Date(pc.period_start).toLocaleString(),
        Type: pc.period_type,
        Camera: cameras.find(c => c.id === pc.camera_id)?.name ?? pc.camera_id,
        Site: sites.find(s => s.id === pc.site_id)?.name ?? pc.site_id,
        'People In': pc.enter_count,
        'People Out': pc.exit_count,
        'Net': pc.enter_count - pc.exit_count,
      }))
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(pcRows), 'People Counting')
    }

    XLSX.writeFile(wb, `analytics-${toDateStr(new Date())}.xlsx`)
    toast('Export downloaded', 'success')
  }, [events, peopleCounts, cameras, sites, role, toast])

  // ── Chart options ──
  const baseTheme = {
    backgroundColor: 'transparent',
    textStyle: { color: '#6b7280', fontFamily: 'inherit' },
  }

  const pieOption = {
    ...baseTheme,
    tooltip: { trigger: 'item' as const, formatter: '{b}: {c} ({d}%)' },
    series: [{
      type: 'pie' as const,
      radius: ['40%', '70%'],
      avoidLabelOverlap: true,
      itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 },
      label: { show: true, fontSize: 11 },
      data: eventTypeCounts.map(d => ({
        ...d,
        itemStyle: { color: EVENT_COLORS[d.type] ?? '#6b7280' },
      })),
    }],
  }

  const eventsBarOption = {
    ...baseTheme,
    tooltip: { trigger: 'axis' as const },
    legend: { top: 0, textStyle: { fontSize: 10, color: '#9ca3af' } },
    grid: { top: 30, left: 40, right: 16, bottom: 24 },
    xAxis: { type: 'category' as const, data: eventsOverTime.days, axisLabel: { fontSize: 10 } },
    yAxis: { type: 'value' as const, axisLabel: { fontSize: 10 } },
    series: eventsOverTime.types.map(type => ({
      name: EVENT_LABELS[type] ?? type,
      type: 'bar' as const,
      stack: 'total',
      emphasis: { focus: 'series' as const },
      itemStyle: { color: EVENT_COLORS[type] ?? '#6b7280' },
      data: eventsOverTime.days.map(d => eventsOverTime.buckets[d]?.[type] ?? 0),
    })),
  }

  const peopleLineOption = {
    ...baseTheme,
    tooltip: { trigger: 'axis' as const },
    legend: { top: 0, textStyle: { fontSize: 10, color: '#9ca3af' } },
    grid: { top: 30, left: 50, right: 16, bottom: 24 },
    xAxis: { type: 'category' as const, data: peopleOverTime.days, axisLabel: { fontSize: 10 } },
    yAxis: { type: 'value' as const, axisLabel: { fontSize: 10 } },
    series: [
      {
        name: 'People In',
        type: 'line' as const,
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        itemStyle: { color: '#10b981' },
        areaStyle: { color: 'rgba(16, 185, 129, 0.1)' },
        data: peopleOverTime.days.map(d => peopleOverTime.data[d]?.enter ?? 0),
      },
      {
        name: 'People Out',
        type: 'line' as const,
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        itemStyle: { color: '#ef4444' },
        areaStyle: { color: 'rgba(239, 68, 68, 0.1)' },
        data: peopleOverTime.days.map(d => peopleOverTime.data[d]?.exit ?? 0),
      },
    ],
  }

  const topCamerasOption = {
    ...baseTheme,
    tooltip: { trigger: 'axis' as const },
    grid: { top: 8, left: 100, right: 24, bottom: 8 },
    xAxis: { type: 'value' as const, axisLabel: { fontSize: 10 } },
    yAxis: {
      type: 'category' as const,
      data: topCameras.map(c => c.name).reverse(),
      axisLabel: { fontSize: 10 },
    },
    series: [{
      type: 'bar' as const,
      data: topCameras.map(c => c.value).reverse(),
      itemStyle: { color: '#6366f1', borderRadius: [0, 4, 4, 0] },
    }],
  }

  // Summary stats
  const totalEvents = events.length
  const totalPeopleIn = peopleCounts.reduce((s, p) => s + p.enter_count, 0)
  const totalPeopleOut = peopleCounts.reduce((s, p) => s + p.exit_count, 0)
  const uniquePlates = new Set(
    events
      .filter(e => e.event_type === 'license_plate')
      .map(e => (e.metadata as Record<string, unknown>).plate_number)
      .filter(Boolean)
  ).size

  const tabs: { key: Tab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'people', label: 'People' },
    { key: 'vehicles', label: 'Vehicles' },
    { key: 'events', label: 'All Events' },
  ]

  const selectCls = 'text-xs px-2 py-1.5 rounded-md border border-gray-200 bg-white text-gray-700 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20'
  const inputCls = 'text-xs px-2 py-1.5 rounded-md border border-gray-200 bg-white text-gray-700 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20'

  if (!miEnabled) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <span className="text-2xl text-gray-400">{'\u2261'}</span>
        </div>
        <h1 className="text-lg font-semibold text-gray-900 mb-1">Analytics Not Activated</h1>
        <p className="text-sm text-gray-500 max-w-sm">
          Analytics and management information features are not enabled for this organization.
          Contact your Redvision administrator to activate this feature.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Analytics</h1>
          <p className="text-xs text-gray-500 mt-0.5">Detection events, people counting, and vehicle intelligence</p>
        </div>
        {canExportData(role) && (
          <button
            onClick={handleExport}
            disabled={loading || (events.length === 0 && peopleCounts.length === 0)}
            className="text-xs px-3 py-1.5 rounded-md bg-brand text-white hover:bg-brand-dark transition-colors disabled:opacity-50 font-medium"
          >
            Export to Excel
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap bg-white border border-gray-200 rounded-lg px-4 py-3">
        {isRvAdmin && orgs.length > 1 && (
          <div>
            <label className="block text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">Organization</label>
            <select
              value={selectedOrgId ?? ''}
              onChange={(e) => { setSelectedOrgId(e.target.value); setSelectedSiteId('all'); setSelectedCameraId('all') }}
              className={selectCls}
            >
              {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="block text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">Site</label>
          <select
            value={selectedSiteId}
            onChange={(e) => { setSelectedSiteId(e.target.value); setSelectedCameraId('all') }}
            className={selectCls}
          >
            <option value="all">All Sites</option>
            {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">Camera</label>
          <select
            value={selectedCameraId}
            onChange={(e) => setSelectedCameraId(e.target.value)}
            className={selectCls}
          >
            <option value="all">All Cameras</option>
            {cameras.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">Period</label>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value as Period)}
            className={selectCls}
          >
            <option value="today">Today</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="custom">Custom Range</option>
          </select>
        </div>
        {period === 'custom' && (
          <>
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">From</label>
              <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">To</label>
              <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className={inputCls} />
            </div>
          </>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`text-xs px-4 py-2 font-medium transition-colors border-b-2 -mb-px ${
              tab === t.key
                ? 'border-brand text-brand'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <p className="text-sm text-gray-400">Loading analytics...</p>
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <SummaryCard label="Total Events" value={totalEvents.toLocaleString()} color="#6366f1" />
            <SummaryCard label="People In" value={totalPeopleIn.toLocaleString()} color="#10b981" />
            <SummaryCard label="People Out" value={totalPeopleOut.toLocaleString()} color="#ef4444" />
            <SummaryCard label="Unique Plates" value={uniquePlates.toLocaleString()} color="#f59e0b" />
          </div>

          {/* Tab content */}
          {tab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ChartCard title="Events by Type">
                {eventTypeCounts.length > 0 ? (
                  <ReactECharts option={pieOption} style={{ height: 280 }} />
                ) : <EmptyChart />}
              </ChartCard>
              <ChartCard title="Events Over Time">
                {eventsOverTime.days.length > 0 ? (
                  <ReactECharts option={eventsBarOption} style={{ height: 280 }} />
                ) : <EmptyChart />}
              </ChartCard>
              <ChartCard title="People Counting Trend">
                {peopleOverTime.days.length > 0 ? (
                  <ReactECharts option={peopleLineOption} style={{ height: 280 }} />
                ) : <EmptyChart message="No people counting data" />}
              </ChartCard>
              <ChartCard title="Most Active Cameras">
                {topCameras.length > 0 ? (
                  <ReactECharts option={topCamerasOption} style={{ height: 280 }} />
                ) : <EmptyChart />}
              </ChartCard>
            </div>
          )}

          {tab === 'people' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ChartCard title="People In vs Out — Daily Trend" wide>
                {peopleOverTime.days.length > 0 ? (
                  <ReactECharts option={peopleLineOption} style={{ height: 320 }} />
                ) : <EmptyChart message="No people counting data. Enable people counting on cameras and poll stats to populate." />}
              </ChartCard>
              <ChartCard title="Net Occupancy">
                {peopleOverTime.days.length > 0 ? (
                  <ReactECharts option={{
                    ...baseTheme,
                    tooltip: { trigger: 'axis' as const },
                    grid: { top: 16, left: 50, right: 16, bottom: 24 },
                    xAxis: { type: 'category' as const, data: peopleOverTime.days, axisLabel: { fontSize: 10 } },
                    yAxis: { type: 'value' as const, axisLabel: { fontSize: 10 } },
                    series: [{
                      type: 'bar' as const,
                      data: peopleOverTime.days.map(d => {
                        const v = (peopleOverTime.data[d]?.enter ?? 0) - (peopleOverTime.data[d]?.exit ?? 0)
                        return { value: v, itemStyle: { color: v >= 0 ? '#10b981' : '#ef4444' } }
                      }),
                    }],
                  }} style={{ height: 320 }} />
                ) : <EmptyChart message="No data" />}
              </ChartCard>
            </div>
          )}

          {tab === 'vehicles' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ChartCard title="Top License Plates Detected">
                {vehicleSummary.topPlates.length > 0 ? (
                  <div className="space-y-1 max-h-[320px] overflow-y-auto">
                    {vehicleSummary.topPlates.map(([plate, count]) => (
                      <div key={plate} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded">
                        <span className="text-sm font-mono font-medium text-gray-900">{plate}</span>
                        <span className="text-xs text-gray-500 bg-white px-2 py-0.5 rounded">{count} time{count > 1 ? 's' : ''}</span>
                      </div>
                    ))}
                  </div>
                ) : <EmptyChart message="No license plate detections" />}
              </ChartCard>
              <ChartCard title="Vehicle Brands Detected">
                {vehicleSummary.topBrands.length > 0 ? (
                  <ReactECharts option={{
                    ...baseTheme,
                    tooltip: { trigger: 'axis' as const },
                    grid: { top: 8, left: 80, right: 24, bottom: 8 },
                    xAxis: { type: 'value' as const, axisLabel: { fontSize: 10 } },
                    yAxis: {
                      type: 'category' as const,
                      data: vehicleSummary.topBrands.map(b => b[0]).reverse(),
                      axisLabel: { fontSize: 10 },
                    },
                    series: [{
                      type: 'bar' as const,
                      data: vehicleSummary.topBrands.map(b => b[1]).reverse(),
                      itemStyle: { color: '#f59e0b', borderRadius: [0, 4, 4, 0] },
                    }],
                  }} style={{ height: 320 }} />
                ) : <EmptyChart message="No vehicle brand data" />}
              </ChartCard>
            </div>
          )}

          {tab === 'events' && (
            <ChartCard title={`Recent Events (${events.length})`}>
              <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b border-gray-200 text-left">
                      <th className="py-2 px-2 font-medium text-gray-500">Time</th>
                      <th className="py-2 px-2 font-medium text-gray-500">Type</th>
                      <th className="py-2 px-2 font-medium text-gray-500">Camera</th>
                      <th className="py-2 px-2 font-medium text-gray-500">Site</th>
                      <th className="py-2 px-2 font-medium text-gray-500">Confidence</th>
                      <th className="py-2 px-2 font-medium text-gray-500">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.slice(0, 200).map(e => {
                      const meta = e.metadata as Record<string, unknown>
                      const details: string[] = []
                      if (meta.plate_number) details.push(`Plate: ${meta.plate_number}`)
                      if (meta.car_brand) details.push(`Brand: ${meta.car_brand}`)
                      if (meta.gender) details.push(`${meta.gender}`)
                      if (meta.age) details.push(`Age: ${meta.age}`)
                      return (
                        <tr key={e.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-1.5 px-2 text-gray-600 whitespace-nowrap">
                            {new Date(e.timestamp).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                          </td>
                          <td className="py-1.5 px-2">
                            <span
                              className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium text-white"
                              style={{ backgroundColor: EVENT_COLORS[e.event_type] ?? '#6b7280' }}
                            >
                              {EVENT_LABELS[e.event_type] ?? e.event_type}
                            </span>
                          </td>
                          <td className="py-1.5 px-2 text-gray-700">{cameras.find(c => c.id === e.camera_id)?.name ?? '—'}</td>
                          <td className="py-1.5 px-2 text-gray-500">{sites.find(s => s.id === e.site_id)?.name ?? '—'}</td>
                          <td className="py-1.5 px-2 text-gray-500">{e.confidence ? `${e.confidence}%` : '—'}</td>
                          <td className="py-1.5 px-2 text-gray-500 truncate max-w-[200px]">{details.join(', ') || '—'}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                {events.length === 0 && <EmptyChart message="No events in the selected range" />}
              </div>
            </ChartCard>
          )}
        </>
      )}
    </div>
  )
}

// ── Sub-components ──

function SummaryCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">{label}</p>
      <p className="text-2xl font-semibold" style={{ color }}>{value}</p>
    </div>
  )
}

function ChartCard({ title, children, wide }: { title: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-4 ${wide ? 'lg:col-span-2' : ''}`}>
      <h3 className="text-xs font-medium text-gray-700 mb-3">{title}</h3>
      {children}
    </div>
  )
}

function EmptyChart({ message = 'No data for the selected filters' }: { message?: string }) {
  return (
    <div className="flex items-center justify-center h-48 text-xs text-gray-400">
      {message}
    </div>
  )
}
